import {
  Avatar,
  Button,
  Card,
  CardContent,
  Grid,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  alpha,
} from '@mui/material';

import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { styled } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { reportError } from '../../lib/sentry';
import { IUser, professions } from '../../lib/interfaces';
import TextFieldWithButton from '../TextFieldWithButton/TextFieldWithButton';
import { canEditContactDetails, isOwnProfile } from './contactEditing';
import type { ContactField } from '../../lib/services/generalService';

import {
  logoutAction,
  updatePatientContactDetails,
  updateUserProfile,
} from '../../store/actions/general';

import type { AppDispatch } from '../../store';

import { RootState } from '../../store/types/sharedTypes';
import { stringAvatar } from '../../lib/helpers/avatar';
import { getFormattedPersonalNumber } from '../../lib/helpers/personalNumber';
import { GENERAL_FONT } from '../../constants/theme';

import DropdownWithButton from '../ToggleFieldWithButton/ToggleFieldWithButton';
import {
  PatientDeletionResponse,
  requestPatientDeletion,
  requestAdminDeletion,
} from '../../lib/services/patientService';
import FlashSnackbar from '../common/FlashSnackbar';
import { hasPermission } from '../../lib/authz';
import AdminPermissionsEditor from '../AdminPermissionEditor/AdminPermissionEditor';

type DeletionType = 'patient' | 'admin';
type DeletionStatus = PatientDeletionResponse['status'];
type AttemptId = PatientDeletionResponse['attemptId'];

type Severity = 'success' | 'warning' | 'error';

export function formatDeletionMessage(
  t: (k: string, o?: Record<string, unknown>) => string,
  status: DeletionStatus,
  name: string,
  attemptId?: AttemptId,
  type: 'patient' | 'admin' = 'patient'
): { severity: Severity; msg: string } {
  const roleText = type === 'patient' ? t('patient') : t('admin');

  switch (status) {
    case 'ok':
      return {
        severity: 'success',
        msg: t('deleteOkBody', { name, role: roleText }),
      };
    case 'partial':
      return {
        severity: 'warning',
        msg:
          t('deletePartialBody', { name, role: roleText }) +
          (attemptId ? `\n${t('attemptId')}: ${attemptId}` : ''),
      };
    default:
      return {
        severity: 'error',
        msg:
          t('deleteFailedBody', { name, role: roleText }) +
          (attemptId ? `\n${t('attemptId')}: ${attemptId}` : ''),
      };
  }
}

const PersonalNumberContainer = styled.div`
  display: ruby-text;
`;
const CONFIRM_PHRASE = 'DELETE';

export interface PatientProfileProps {
  patient: IUser | null;
  onClick?: (value?: string) => void;
}

const getRemoveConfirmationKey = (
  isSelf: boolean,
  deletionType: DeletionType | null
) => {
  if (isSelf) {
    return 'areYouSureRemoveSelf';
  }
  if (deletionType === 'admin') {
    return 'areYouSureRemoveAdminPrefix';
  }
  return 'areYouSureRemovePrefix';
};

const PatientProfile = (props: PatientProfileProps) => {
  const { patient } = props;

  const dispatch = useDispatch<AppDispatch>();

  const { sessionUser } = useSelector((state: RootState) => state.general);

  const { t } = useTranslation();
  const [currentlyEditingField, setCurrentlyEditingField] = useState('');
  const [tooltipOpen, setTooltipOpen] = React.useState(false);
  const [copyFeedback, setCopyFeedback] = React.useState('');

  const editField = (fieldName: string, data: string) => {
    if (patient?._id) {
      dispatch(updateUserProfile(fieldName, data, patient._id));
    }
  };

  const isAdmin = patient?.isAdmin;
  const isPatient = patient?.isPatient;
  const canEdit = isOwnProfile(sessionUser, patient);
  const canEditContact = canEditContactDetails(sessionUser, patient);

  const editContactField = (fieldName: ContactField, data: string) => {
    if (!patient?._id) return;

    if (canEdit) {
      editField(fieldName, data);
      return;
    }

    dispatch(updatePatientContactDetails(fieldName, data, patient._id));
  };

  const canDeletePatient = hasPermission(sessionUser, 'patient:delete');
  const canDeleteAdmin = hasPermission(sessionUser, 'admin:delete');
  const allowRemovePatient =
    !!patient && !!patient.isPatient && canDeletePatient;
  const allowRemoveAdmin = !!patient && !!patient.isAdmin && canDeleteAdmin;

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [deletionType, setDeletionType] = useState<DeletionType | null>(null);

  const [flashOpen, setFlashOpen] = useState(false);
  const [flashMsg, setFlashMsg] = useState('');
  const [flashSeverity, setFlashSeverity] = useState<Severity>('success');
  const navigate = useNavigate();
  const isSuper = !!sessionUser?.isSuperAdmin;
  const isTargetAdmin = !!patient?.isAdmin;
  const isSelf = patient?._id === sessionUser?._id;

  const fullName =
    `${patient?.firstName ?? ''} ${patient?.lastName ?? ''}`.trim();

  const canConfirm = confirmText.trim() === CONFIRM_PHRASE;

  const handleOpenConfirm = (type: DeletionType) => {
    if (type === 'patient' && !allowRemovePatient) return;
    if (type === 'admin' && !allowRemoveAdmin) return;
    setDeletionType(type);
    setConfirmText('');
    setConfirmOpen(true);
  };

  const handleCloseConfirm = () => {
    if (confirming) return;
    setConfirmOpen(false);
    setConfirmText('');
    setDeletionType(null);
  };

  const handleConfirm = async () => {
    if (!patient || !canConfirm || !deletionType) return;
    try {
      setConfirming(true);

      const deleteFn =
        deletionType === 'patient'
          ? requestPatientDeletion
          : requestAdminDeletion;
      const data = await deleteFn(patient._id, confirmText.trim());

      const quotedName = `"${fullName}"`;
      const { severity, msg } = formatDeletionMessage(
        t,
        data.status,
        quotedName,
        data.attemptId
      );

      if (data.status === 'ok' || data.status === 'partial') {
        setConfirmOpen(false);

        if (isSelf) {
          dispatch(logoutAction(navigate));
          return;
        }

        const redirectPath =
          deletionType === 'patient'
            ? '/dashboard/patients'
            : '/dashboard/admins';
        navigate(redirectPath, {
          state: { flash: { severity, msg } },
          replace: true,
        });
        return;
      }

      setFlashSeverity(severity);
      setFlashMsg(msg);
      setFlashOpen(true);
    } catch (e: unknown) {
      const quotedName = `"${fullName}"`;
      const { severity, msg } = formatDeletionMessage(t, 'failed', quotedName);
      setFlashSeverity(severity);
      setFlashMsg(msg);
      setFlashOpen(true);

      reportError(e);
      console.error('Delete error:', e);
    } finally {
      setConfirming(false);
    }
  };

  const handleClose = () => {
    setTooltipOpen(false);
  };

  const handleCopy = () => {
    const formattedNumber = getFormattedPersonalNumber(
      patient?.personalNumber ?? ''
    );
    navigator.clipboard
      .writeText(formattedNumber || 'Personal number not found')
      .then(() => {
        setCopyFeedback(t('copiedSuccessfully'));
        setTooltipOpen(true);
      })
      .catch((err) =>
        reportError(err, { level: 'warning', tags: { area: 'clipboard' } })
      );
  };

  return (
    <>
      <Grid container>
        <Grid item xs={3}>
          <Card
            sx={{
              width: '100%',
              padding: '20px',
              marginRight: '20px',
              cursor: 'default',
            }}
          >
            <CardContent style={{ cursor: 'default' }}>
              <Avatar
                style={{
                  height: '200px',
                  width: '200px',
                  margin: 'auto',
                  cursor: 'default',
                }}
                {...stringAvatar(`${patient?.firstName} ${patient?.lastName}`)}
              />

              <Typography
                style={{
                  fontSize: '20px',
                  fontFamily: GENERAL_FONT,
                  fontWeight: 'bold',
                  marginTop: '20px',
                }}
              >
                {`${patient?.firstName} ${patient?.lastName}`}
              </Typography>
              <PersonalNumberContainer>
                <Typography
                  id="personalNumberId"
                  style={{
                    fontSize: '14px',
                    fontFamily: GENERAL_FONT,
                    opacity: '0.5',
                  }}
                >
                  {getFormattedPersonalNumber(patient?.personalNumber ?? '')}
                </Typography>
                <Tooltip
                  placement="top"
                  open={tooltipOpen}
                  onClose={handleClose}
                  title={copyFeedback}
                  leaveDelay={500}
                >
                  <ContentCopyIcon
                    onClick={handleCopy}
                    style={{
                      marginLeft: '6px',
                      float: 'right',
                      fontSize: '16px',
                      cursor: 'pointer',
                    }}
                  />
                </Tooltip>
              </PersonalNumberContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={9} style={{ paddingLeft: '20px' }}>
          <Grid container>
            <Grid item xs={12}>
              <TextFieldWithButton
                noEdit={!canEditContact}
                label={t('email')}
                data={patient?.email ?? ''}
                onSave={(data) => editContactField('email', data)}
                onEditStart={(data) => setCurrentlyEditingField(data)}
                currentlyEdited={currentlyEditingField === t('email')}
              />
            </Grid>

            <Grid item style={{ paddingRight: '10px' }} xs={6}>
              <TextFieldWithButton
                noEdit={!canEditContact}
                label={t('phone')}
                data={patient?.phoneNumber ?? ''}
                onSave={(data) => editContactField('phoneNumber', data)}
                onEditStart={(data) => setCurrentlyEditingField(data)}
                currentlyEdited={currentlyEditingField === t('phone')}
              />
            </Grid>

            {isAdmin && (
              <Grid style={{ paddingLeft: '10px' }} item xs={6}>
                <DropdownWithButton
                  noEdit={!canEdit}
                  label={t('profession')}
                  value={patient?.profession ?? ''}
                  options={professions.map((profession) => ({
                    value: profession,
                    label: t(profession),
                  }))}
                  currentlyEdited={currentlyEditingField === t('profession')}
                  onSave={(data) => editField('profession', data)}
                  onEditStart={() => setCurrentlyEditingField('profession')}
                />
              </Grid>
            )}

            {isPatient && (
              <Grid
                item
                style={{ paddingLeft: isAdmin ? 0 : '10px' }}
                xs={isAdmin ? 12 : 6}
              >
                <DropdownWithButton
                  noEdit={!canEdit}
                  label={t('menstruating')}
                  value={patient?.isMenstruating ? 'true' : 'false'}
                  options={[
                    { value: 'true', label: t('yes') },
                    { value: 'false', label: t('no') },
                  ]}
                  currentlyEdited={
                    currentlyEditingField === t('isMenstruating')
                  }
                  onSave={(data) => editField('isMenstruating', data)}
                  onEditStart={() => setCurrentlyEditingField('isMenstruating')}
                />
              </Grid>
            )}
          </Grid>
          {isSuper && isTargetAdmin && (
            <AdminPermissionsEditor
              target={{
                _id: patient._id,
                isAdmin: patient.isAdmin,
                permissions: patient.permissions ?? [],
                firstName: patient.firstName,
                lastName: patient.lastName,
              }}
            />
          )}

          {allowRemovePatient && (
            <Box
              sx={(theme) => ({
                mt: 2,
                p: 1.5,
                borderRadius: 2,
                bgcolor: alpha(
                  theme.palette.error.main,
                  theme.palette.mode === 'dark' ? 0.1 : 0.06
                ),
              })}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ flex: 1 }}
                >
                  {t('removePatientHelp')}
                </Typography>

                <Button
                  variant="outlined"
                  color="error"
                  size="medium"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => handleOpenConfirm('patient')}
                  disabled={!patient?._id}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 1,
                    px: 3,
                    py: 1.1,
                  }}
                >
                  {t('removePatient')}
                </Button>
              </Box>
            </Box>
          )}

          {allowRemoveAdmin && (
            <Box
              sx={(theme) => ({
                mt: 2,
                p: 1.5,
                borderRadius: 2,
                bgcolor: alpha(
                  theme.palette.error.main,
                  theme.palette.mode === 'dark' ? 0.1 : 0.06
                ),
              })}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ flex: 1 }}
                >
                  {t('removeAdminHelp')}
                </Typography>

                <Button
                  variant="outlined"
                  color="error"
                  size="medium"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => handleOpenConfirm('admin')}
                  disabled={!patient?._id}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 1,
                    px: 3,
                    py: 1.1,
                  }}
                >
                  {t('removeAdmin')}
                </Button>
              </Box>
            </Box>
          )}
        </Grid>
      </Grid>
      <Dialog
        open={confirmOpen}
        onClose={handleCloseConfirm}
        fullWidth
        maxWidth="sm"
        disableEscapeKeyDown={confirming}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !canConfirm) e.preventDefault();
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {deletionType === 'admin' ? t('removeAdmin') : t('removePatient')}
        </DialogTitle>
        <Box
          component="form"
          autoComplete="off"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            if (canConfirm && !confirming) handleConfirm();
          }}
        >
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {t(getRemoveConfirmationKey(isSelf, deletionType))}{' '}
              {!isSelf && (
                <strong>
                  <em>{fullName}</em>
                </strong>
              )}
              ?
            </Typography>
            {isSelf && (
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                {t('selfDeleteWarning')}
              </Typography>
            )}
            <TextField
              autoFocus
              fullWidth
              type="text"
              name="confirmPhrase"
              autoComplete="off"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              disabled={confirming}
              spellCheck={false}
              inputProps={{
                autoComplete: 'off',
                autoCorrect: 'off',
                autoCapitalize: 'off',
                'data-testid': 'delete-confirm-input',
              }}
              sx={{
                mt: 1,
                '& .MuiOutlinedInput-root': { minHeight: 48 },
                '& .MuiOutlinedInput-input': { py: 1 },
              }}
              helperText={t('typeDELETEToConfirm', { phrase: CONFIRM_PHRASE })}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseConfirm} disabled={confirming}>
              {t('cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="error"
              disabled={!canConfirm || confirming}
            >
              {confirming ? t('removing') : t('remove')}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <FlashSnackbar
        open={flashOpen}
        msg={flashMsg}
        severity={flashSeverity}
        onClose={() => {
          setFlashOpen(false);
          setFlashMsg('');
        }}
      />
    </>
  );
};

export default PatientProfile;
